import React, { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { QuestionMarkCircledIcon, Cross1Icon, ArrowUpIcon, ArrowDownIcon, CheckIcon, BarChartIcon, MagicWandIcon } from "@radix-ui/react-icons";
import confetti from "canvas-confetti";
import Words from "./words";
import answers from "./answers";
import { submitGuess } from "./api";

const getTodayDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStoredValue = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item !== null ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
};

const getGameHistory = () => {
  return getStoredValue('gameHistory', {});
};

const saveGameData = (date, guesses, answer) => {
  const history = getGameHistory();
  const won = guesses.length > 0 && guesses[guesses.length - 1] === answer;
  history[date] = {
    guesses: [...guesses],
    won,
    guessCount: guesses.length
  };
  setStoredValue('gameHistory', history);
  setStoredValue('lastPlayedDate', date);
};

const getGameDataForDate = (date) => {
  const history = getGameHistory();
  return history[date] || null;
};

const getStatistics = () => {
  const history = getGameHistory();
  const dates = Object.keys(history).sort();
  
  if (dates.length === 0) {
    return {
      played: 0,
      averageGuesses: 0,
      currentStreak: 0,
      maxStreak: 0
    };
  }
  
  const played = dates.length;
  const totalGuesses = dates.reduce((sum, date) => sum + history[date].guessCount, 0);
  const averageGuesses = totalGuesses / played;
  
  const today = getTodayDate();
  const todayDate = new Date(today + 'T00:00:00');
  
  let currentStreak = 0;
  let checkDate = new Date(todayDate);
  
  while (dates.includes(checkDate.toISOString().split('T')[0])) {
    currentStreak++;
    checkDate.setDate(checkDate.getDate() - 1);
  }
  
  if (dates[dates.length - 1] !== today) {
    currentStreak = 0;
  }
  
  let maxStreak = 0;
  let tempStreak = 1;
  
  for (let i = 1; i < dates.length; i++) {
    const prevDate = new Date(dates[i - 1] + 'T00:00:00');
    const currDate = new Date(dates[i] + 'T00:00:00');
    const daysDiff = Math.floor((currDate - prevDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === 1) {
      tempStreak++;
    } else {
      maxStreak = Math.max(maxStreak, tempStreak);
      tempStreak = 1;
    }
  }
  maxStreak = Math.max(maxStreak, tempStreak);
  
  return {
    played,
    averageGuesses: Math.round(averageGuesses * 10) / 10,
    currentStreak,
    maxStreak
  };
};

export default function App() {
  const [day, setDay] = useState();
  const [answer, setAnswer] = useState();
  const [currentGuess, setCurrentGuess] = useState("");
  const [guesses, setGuesses] = useState([]);
  const [showHelp, setShowHelp] = useState(() => {
    return !getStoredValue('helpModalDismissed', false);
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [practiceMode, setPracticeMode] = useState(false);
  const [practiceAnswer, setPracticeAnswer] = useState(null);
  const [easyMode, setEasyMode] = useState(() => {
    return getStoredValue('easyMode', false);
  });
  const [shakeInput, setShakeInput] = useState(false);
  const [highlightedGuess, setHighlightedGuess] = useState(null);
  const [highlightKey, setHighlightKey] = useState(0);
  const [countdown, setCountdown] = useState('');
  const boardRef = useRef(null);
  const guessesRef = useRef(guesses);

  const currentAnswer = practiceMode ? practiceAnswer : answer;
  const currentAnswerRef = useRef(currentAnswer);

  useEffect(() => {
    currentAnswerRef.current = currentAnswer;
  }, [currentAnswer]);

  useEffect(() => {
    guessesRef.current = guesses;
  }, [guesses]);

  useEffect(() => {
    const startDate = new Date("2025-12-24");
    const day = getDay(startDate);
    
    setDay(day);
    const today = getTodayDate();
    setAnswer(answers[today]);
    
    const gameData = getGameDataForDate(today);
    if (gameData) {
      setGuesses(gameData.guesses);
      if (gameData.won) {
        setShowSuccess(true);
      }
    }
  }, []);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const diff = tomorrow - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmitGuess = async (guess) => {
    const lowerGuess = guess.toLowerCase();
    
    if (lowerGuess.length !== 5) {
      return { valid: false, reason: 'length' };
    }
    
    if (Words.indexOf(lowerGuess) === -1) {
      return { valid: false, reason: 'invalid' };
    }
    
    if (guessesRef.current.includes(lowerGuess)) {
      return { valid: false, reason: 'duplicate' };
    }
    
    const newGuesses = [...guessesRef.current, lowerGuess];
    setGuesses(newGuesses);
    
    if (practiceMode) {
      if (lowerGuess === currentAnswerRef.current) {
        setShowSuccess(true);
      }
    } else {
      const today = getTodayDate();
      const result = await submitGuess(today, lowerGuess);
      saveGameData(today, newGuesses, currentAnswerRef.current);
      
      if (result.isCorrect) {
        setShowSuccess(true);
      }
    }
    
    return { valid: true };
  };

  const handleKeyPress = async (key) => {
    if (showHelp || isGameWon) return;
    
    if (key === "backspace") {
      setCurrentGuess((prev) => prev.slice(0, -1));
      return;
    }
    
    if (key === "enter") {
      if (currentGuess.length === 5) {
        const result = await handleSubmitGuess(currentGuess);
        if (result.valid) {
          setCurrentGuess("");
        } else {
          if (result.reason === 'duplicate') {
            flushSync(() => {
              setHighlightKey(k => k + 1);
              setHighlightedGuess(currentGuess.toLowerCase());
            });
            setTimeout(() => {
              setHighlightedGuess(null);
              setCurrentGuess("");
            }, 700);
          } else {
            setShakeInput(true);
            setTimeout(() => {
              setShakeInput(false);
              setCurrentGuess("");
            }, 500);
          }
        }
      }
      return;
    }
    
    if (key.length === 1 && /[a-z]/.test(key)) {
      if (currentGuess.length < 5) {
        setCurrentGuess((prev) => prev + key);
      }
    }
  };

  const isGameWon = guesses.length > 0 && guesses[guesses.length - 1] === currentAnswer;

  const handleDismissHelp = () => {
    setShowHelp(false);
    setStoredValue('helpModalDismissed', true);
  };

  const startPracticeMode = () => {
    const randomWord = Words[Math.floor(Math.random() * Words.length)];
    setPracticeAnswer(randomWord);
    setPracticeMode(true);
    setGuesses([]);
    setCurrentGuess("");
    setShowSuccess(false);
  };

  useEffect(() => {
    const handleKeyDown = async (event) => {
      if (showHelp) return;
      if (isGameWon) return;
      
      const key = event.key.toLowerCase();
      
      if (key === "backspace") {
        setCurrentGuess((prev) => prev.slice(0, -1));
        return;
      }
      
      if (key === "enter") {
        const guess = currentGuess;
        if (guess.length === 5) {
          const result = await handleSubmitGuess(guess);
          if (!result.valid) {
            if (result.reason === 'duplicate') {
              flushSync(() => {
                setHighlightKey(k => k + 1);
                setHighlightedGuess(guess.toLowerCase());
              });
              setTimeout(() => {
                setHighlightedGuess(null);
                setCurrentGuess("");
              }, 700);
            } else {
              flushSync(() => {
                setShakeInput(true);
              });
              setTimeout(() => {
                setShakeInput(false);
                setCurrentGuess("");
              }, 500);
            }
          } else {
            setCurrentGuess("");
          }
        }
        return;
      }
      
      if (key.length === 1 && /[a-z]/.test(key)) {
        setCurrentGuess((prev) => {
          if (prev.length < 5) {
            return prev + key;
          }
          return prev;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showHelp, isGameWon, currentGuess]);

  useEffect(() => {
    if (!showHelp && boardRef.current) {
      boardRef.current.focus();
    }
  }, [showHelp]);

  useEffect(() => {
    if (isGameWon) {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'fixed';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '2000';
      document.body.appendChild(canvas);

      const myConfetti = confetti.create(canvas, { resize: true });
      myConfetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      }).then(() => {
        document.body.removeChild(canvas);
      });
    }
  }, [isGameWon]);

  const generateVisualization = () => {
    if (!currentAnswer || guesses.length === 0) return '';
    
    const belowGuesses = guesses
      .filter(guess => guess < currentAnswer)
      .map(guess => getGuessData(guess, guesses))
      .filter(data => data !== null)
      .sort((a, b) => b.distance - a.distance);
    
    const aboveGuesses = guesses
      .filter(guess => guess > currentAnswer)
      .map(guess => getGuessData(guess, guesses))
      .filter(data => data !== null)
      .sort((a, b) => a.distance - b.distance);
    
    const maxLines = 8;
    const totalGuesses = belowGuesses.length + aboveGuesses.length;
    
    let belowLines = 0;
    let aboveLines = 0;
    
    if (totalGuesses <= maxLines) {
      belowLines = belowGuesses.length;
      aboveLines = aboveGuesses.length;
    } else {
      if (belowGuesses.length <= aboveGuesses.length) {
        belowLines = belowGuesses.length;
        aboveLines = Math.min(aboveGuesses.length, maxLines - belowLines);
      } else {
        aboveLines = aboveGuesses.length;
        belowLines = Math.min(belowGuesses.length, maxLines - aboveLines);
      }
    }
    
    const lines = [];
    
    for (let i = 0; i < belowLines; i++) {
      const data = belowGuesses[i];
      if (data) {
        const dots = Math.min(8, Math.max(1, Math.round((data.distancePercentage / 100) * 8)));
        lines.push('🟢'.repeat(dots));
      }
    }
    
    for (let i = 0; i < aboveLines; i++) {
      const data = aboveGuesses[i];
      if (data) {
        const dots = Math.min(8, Math.max(1, Math.round((data.distancePercentage / 100) * 8)));
        lines.push('🔴'.repeat(dots));
      }
    }
    
    return lines.join('\n');
  };

  const composeShareText = () => {
    const visualization = generateVisualization();
    return `Abcdle ${day} - ${guesses.length} guesses

${visualization}

Have a go at https://abcdle.com`;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ text: composeShareText() });
    } else {
      navigator.clipboard.writeText(composeShareText());
    }
  };

  const aboveGuesses = (guesses) => {
    return (
      [...guesses]
        .sort()
        .filter((guess) => guess > currentAnswer)
    );
  };

  const belowGuesses = (guesses) => {
    return (
      [...guesses]
        .sort()
        .filter((guess) => guess < currentAnswer)
    );
  };

  const getDay = (startDate) => {
    const timeDelta = Date.now() - startDate;
    const daysDelta = timeDelta / (1000 * 3600 * 24);

    return Math.ceil(daysDelta);
  };

  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const getGuessData = (guess, allGuesses = []) => {
    if (!currentAnswer) return null;
    const answerIndex = Words.indexOf(currentAnswer);
    const guessIndex = Words.indexOf(guess);
    const distance = Math.abs(guessIndex - answerIndex);
    const isAbove = guessIndex < answerIndex;
    
    let maxDistance;
    if (isAbove) {
      maxDistance = answerIndex;
    } else {
      maxDistance = Words.length - 1 - answerIndex;
    }
    
    if (maxDistance === 0) {
      return {
        guess,
        guessIndex,
        answerIndex,
        distance,
        distancePercentage: 0,
        backgroundColor: isAbove ? 'rgba(144, 238, 144, 0.3)' : 'rgba(255, 107, 107, 0.3)',
        isAbove
      };
    }
    
    const logDistance = Math.log10(distance + 1);
    const logMaxDistance = Math.log10(maxDistance + 1);
    
    const distancePercentage = (logDistance / logMaxDistance) * 100;
    const backgroundColor = isAbove ? 'rgba(144, 238, 144, 0.3)' : 'rgba(255, 107, 107, 0.3)';
    
    return {
      guess,
      guessIndex,
      answerIndex,
      distance,
      distancePercentage,
      backgroundColor,
      isAbove
    };
  };

  const getConsecutiveCorrectCount = (guess, targetAnswer = null) => {
    const answerToUse = targetAnswer || currentAnswer;
    if (!answerToUse || !guess) return 0;
    let count = 0;
    for (let i = 0; i < Math.min(guess.length, answerToUse.length); i++) {
      if (guess[i] === answerToUse[i]) {
        count++;
      } else {
        break;
      }
    }
    return count;
  };

  return (
    <>
      <nav 
        className="main-nav"
        style={{
          width: '100%',
          backgroundColor: 'white',
          borderBottom: '1px solid #ccc',
          padding: '0.5rem 0',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div className="nav-col-1"></div>
        <div className="nav-col-2">
          <div 
            className="nav-logo"
            style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              fontFamily: "'Domine', serif"
            }}
          >
            Abcdle
          </div>
        </div>
        <div className="nav-col-3">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            paddingRight: '1rem'
          }}>
            <button
              onClick={() => setShowHints(true)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
            >
              <MagicWandIcon style={{ width: '24px', height: '24px' }} />
            </button>
            <button
              onClick={() => setShowStatistics(true)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
            >
              <BarChartIcon style={{ width: '24px', height: '24px' }} />
            </button>
            <button
              onClick={() => setShowHelp(true)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
            >
              <QuestionMarkCircledIcon style={{ width: '24px', height: '24px' }} />
            </button>
          </div>
        </div>
      </nav>
      {showHelp && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={handleDismissHelp}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '1em',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleDismissHelp}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
            >
              <Cross1Icon style={{ width: '24px', height: '24px' }} />
            </button>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontFamily: 'sans-serif' }}>How to Play</h2>
            <p style={{ marginBottom: '1rem' }}>
              Guess the word in unlimited tries.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Each guess must be a valid 5-letter word.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Use the arrows and colored bars to guide your guesses: green means the answer is after your guess (toward 'z'), red means it's before (toward 'a').
            </p>
            <p style={{ marginBottom: '1.5rem' }}>
              The number with the arrow shows how many positions away the correct word is. For example, "146 ↓" means the correct word is 146 positions after your guess alphabetically.
            </p>
            
            <div style={{ marginBottom: '2rem' }}>
              <div>
                <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div 
                      style={{
                        position: 'relative',
                        width: '100%',
                        minHeight: '50px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div 
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '25%',
                          backgroundColor: 'rgba(144, 238, 144, 0.3)',
                          borderRight: '2px solid #90EE90',
                          zIndex: 0
                        }}
                      />
                      <div 
                        className="guess-word"
                        style={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {'alpha'.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                      </div>
                      <span className="guess-indicator" style={{ position: 'absolute', right: '1em', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem', zIndex: 2, height: '100%', justifyContent: 'flex-end' }}>
                        {formatNumber(146)} <ArrowDownIcon style={{ width: '16px', height: '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div 
                      style={{
                        position: 'relative',
                        width: '100%',
                        minHeight: '50px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div 
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '5%',
                          backgroundColor: 'rgba(144, 238, 144, 0.3)',
                          borderRight: '2px solid #90EE90',
                          zIndex: 0
                        }}
                      />
                      <div 
                        className="guess-word"
                        style={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {'brave'.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                      </div>
                      <span className="guess-indicator" style={{ position: 'absolute', right: '1em', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem', zIndex: 2, height: '100%', justifyContent: 'flex-end' }}>
                        {formatNumber(4)} <ArrowDownIcon style={{ width: '16px', height: '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div 
                      style={{
                        position: 'relative',
                        width: '100%',
                        minHeight: '50px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div 
                        className="guess-word"
                        style={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {'bread'.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                      </div>
                      <span className="guess-indicator" style={{ position: 'absolute', right: '1em', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem', zIndex: 2, height: '100%', justifyContent: 'flex-end' }}>
                        <CheckIcon style={{ width: '16px', height: '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div 
                      style={{
                        position: 'relative',
                        width: '100%',
                        minHeight: '50px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div 
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '5%',
                          backgroundColor: 'rgba(255, 107, 107, 0.3)',
                          borderRight: '2px solid #FF6B6B',
                          zIndex: 0
                        }}
                      />
                      <div 
                        className="guess-word"
                        style={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {'break'.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                      </div>
                      <span className="guess-indicator" style={{ position: 'absolute', right: '1em', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem', zIndex: 2, height: '100%', justifyContent: 'flex-end' }}>
                        {formatNumber(1)} <ArrowUpIcon style={{ width: '16px', height: '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                  <div style={{ position: 'relative', width: '100%' }}>
                    <div 
                      style={{
                        position: 'relative',
                        width: '100%',
                        minHeight: '50px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <div 
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '75%',
                          backgroundColor: 'rgba(255, 107, 107, 0.3)',
                          borderRight: '2px solid #FF6B6B',
                          zIndex: 0
                        }}
                      />
                      <div 
                        className="guess-word"
                        style={{
                          position: 'relative',
                          zIndex: 1
                        }}
                      >
                        {'zebra'.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                      </div>
                      <span className="guess-indicator" style={{ position: 'absolute', right: '1em', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem', zIndex: 2, height: '100%', justifyContent: 'flex-end' }}>
                        {formatNumber(3278)} <ArrowUpIcon style={{ width: '16px', height: '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showHints && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowHints(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '1em',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowHints(false)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.25rem 0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
            >
              <Cross1Icon style={{ width: '24px', height: '24px' }} />
            </button>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontFamily: 'sans-serif' }}>Hints</h2>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: '0.25rem', fontSize: '1.1rem', fontFamily: 'sans-serif' }}>Easy Mode</h3>
                  <p style={{ marginBottom: 0, fontSize: '0.9rem', color: '#666' }}>
                    Underline matching letters at the start of your guesses
                  </p>
                </div>
                <label className="easy-mode-toggle-label">
                  <input
                    type="checkbox"
                    className="easy-mode-checkbox"
                    checked={easyMode}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setEasyMode(newValue);
                      setStoredValue('easyMode', newValue);
                    }}
                  />
                </label>
              </div>
              <div style={{ marginBottom: '2rem' }}>
                <div>
                  <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <div 
                        style={{
                          position: 'relative',
                          width: '100%',
                          minHeight: '50px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div 
                          className="guess-word"
                          style={{
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          {(() => {
                            const exampleGuess = 'brave';
                            const exampleAnswer = 'bread';
                            const correctCount = getConsecutiveCorrectCount(exampleGuess, exampleAnswer);
                            return exampleGuess.split('').map((char, idx) => (
                              <div key={idx} className="guess-char">
                                {char}
                              </div>
                            )).concat(
                              correctCount > 0 ? (
                                <div
                                  key="underline"
                                  style={{
                                    position: 'absolute',
                                    left: '1em',
                                    bottom: 0,
                                    width: `calc(${correctCount * 1.2 + (correctCount - 1) * 0.25}em)`,
                                    height: '2px',
                                    backgroundColor: '#000',
                                    pointerEvents: 'none'
                                  }}
                                />
                              ) : null
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <div 
                        style={{
                          position: 'relative',
                          width: '100%',
                          minHeight: '50px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div 
                          className="guess-word"
                          style={{
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          {(() => {
                            const exampleGuess = 'bream';
                            const exampleAnswer = 'bread';
                            const correctCount = getConsecutiveCorrectCount(exampleGuess, exampleAnswer);
                            return exampleGuess.split('').map((char, idx) => (
                              <div key={idx} className="guess-char">
                                {char}
                              </div>
                            )).concat(
                              correctCount > 0 ? (
                                <div
                                  key="underline"
                                  style={{
                                    position: 'absolute',
                                    left: '1em',
                                    bottom: 0,
                                    width: `calc(${correctCount * 1.2 + (correctCount - 1) * 0.25}em)`,
                                    height: '2px',
                                    backgroundColor: '#000',
                                    pointerEvents: 'none'
                                  }}
                                />
                              ) : null
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', gap: 0, alignItems: 'center', position: 'relative', width: '100%', maxWidth: '400px' }}>
                    <div style={{ position: 'relative', width: '100%' }}>
                      <div 
                        style={{
                          position: 'relative',
                          width: '100%',
                          minHeight: '50px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <div 
                          className="guess-word"
                          style={{
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          {'bread'.split('').map((char, idx) => (
                            <div key={idx} className="guess-char">
                              {char}
                            </div>
                          ))}
                        </div>
                        <span className="guess-indicator" style={{ position: 'absolute', right: '1em', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.25rem', zIndex: 2, height: '100%', justifyContent: 'flex-end' }}>
                          <CheckIcon style={{ width: '16px', height: '16px' }} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showStatistics && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowStatistics(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '1em', position: 'relative' }}>
              <button
                onClick={() => setShowStatistics(false)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <Cross1Icon style={{ width: '24px', height: '24px' }} />
              </button>
              <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontFamily: 'sans-serif' }}>Statistics</h2>
              {(() => {
                const stats = getStatistics();
                return (
                  <div>
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                        {stats.played}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Played</div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                        {stats.averageGuesses}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Average Guesses</div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                        {stats.currentStreak}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Current Streak</div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                        {stats.maxStreak}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Max Streak</div>
                    </div>
                  </div>
                );
              })()}
            </div>
            {day && (
              <div style={{ 
                paddingTop: '1rem',
                paddingBottom: '1rem',
                borderTop: '1px solid #e0e0e0',
                fontSize: '0.85rem',
                color: '#999',
                textAlign: 'center',
                fontFamily: "'Domine', serif"
              }}>
                Abcdle #{day}
              </div>
            )}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: 'calc(100vh - 80px)', padding: 0 }}>
        <div className="game-board-container" style={{ alignItems: 'center' }}>
          {guesses
            .filter(guess => guess < currentAnswer)
            .map(guess => getGuessData(guess, guesses))
            .filter(data => data !== null)
            .sort((a, b) => b.distance - a.distance)
            .map((data, guessIndex) => {
              const isHighlighted = highlightedGuess === data.guess;
              return (
                <div key={isHighlighted ? `${data.guess}-${highlightKey}` : `${data.guess}-${guessIndex}`} className="guess-row">
                  <div className="guess-container">
                    <div className={`guess ${isHighlighted ? 'highlight' : ''}`}>
                      <div 
                        className="guess-background"
                        style={{
                          width: `${data.distancePercentage}%`,
                          backgroundColor: data.backgroundColor,
                          borderRightColor: data.isAbove ? '#90EE90' : '#FF6B6B'
                        }}
                      />
                      <div className="guess-word" style={{ position: 'relative' }}>
                        {data.guess.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                        {easyMode && (() => {
                          const correctCount = getConsecutiveCorrectCount(data.guess);
                          if (correctCount === 0) return null;
                          const charWidth = 1.2;
                          const gapWidth = 0.25;
                          const totalWidth = correctCount * charWidth + (correctCount - 1) * gapWidth;
                          return (
                            <div
                              style={{
                                position: 'absolute',
                                left: '1em',
                                bottom: 0,
                                width: `calc(${totalWidth}em)`,
                                height: '2px',
                                backgroundColor: '#000',
                                pointerEvents: 'none'
                              }}
                            />
                          );
                        })()}
                      </div>
                      <span className="guess-indicator">
                        {formatNumber(data.distance)} <ArrowDownIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          
          {!isGameWon && (
            <div className="guess-row">
              <div className="guess-container">
                <div 
                  ref={boardRef}
                  className={`guess guess-current ${shakeInput ? 'shake' : ''}`}
                  tabIndex={-1}
                  style={{
                    backgroundColor: '#f5f5f5',
                    outline: 'none',
                    border: 'none'
                  }}
                >
                  <div className="guess-word">
                    {((currentGuess || '').padEnd(5, ' ').toUpperCase()).split('').map((char, idx) => (
                      <div key={idx} className="guess-char">
                        {char}
                      </div>
                    ))}
                  </div>
                  <span className="guess-indicator">
                    Guess #{guesses.length + 1}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {guesses
            .filter(guess => guess === currentAnswer)
            .map((guess, guessIndex) => {
              const isHighlighted = highlightedGuess === guess;
              return (
                <div key={isHighlighted ? `${guess}-${highlightKey}` : `${guess}-correct-${guessIndex}`} className="guess-row">
                  <div className="guess-container">
                    <div className={`guess ${isHighlighted ? 'highlight' : ''}`}>
                      <div className="guess-word">
                        {guess.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                      </div>
                      <span className="guess-indicator">
                        <CheckIcon style={{ width: '16px', height: '16px' }} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          
          
          {guesses
            .filter(guess => guess > currentAnswer)
            .map(guess => getGuessData(guess, guesses))
            .filter(data => data !== null)
            .sort((a, b) => a.distance - b.distance)
            .map((data, guessIndex) => {
              const isHighlighted = highlightedGuess === data.guess;
              return (
                <div key={isHighlighted ? `${data.guess}-${highlightKey}` : `${data.guess}-${guessIndex}`} className="guess-row">
                  <div className="guess-container">
                    <div className={`guess ${isHighlighted ? 'highlight' : ''}`}>
                      <div 
                        className="guess-background"
                        style={{
                          width: `${data.distancePercentage}%`,
                          backgroundColor: data.backgroundColor,
                          borderRightColor: data.isAbove ? '#90EE90' : '#FF6B6B'
                        }}
                      />
                      <div className="guess-word" style={{ position: 'relative' }}>
                        {data.guess.split('').map((char, idx) => (
                          <div key={idx} className="guess-char">
                            {char}
                          </div>
                        ))}
                        {easyMode && (() => {
                          const correctCount = getConsecutiveCorrectCount(data.guess);
                          if (correctCount === 0) return null;
                          const charWidth = 1.2;
                          const gapWidth = 0.25;
                          const totalWidth = correctCount * charWidth + (correctCount - 1) * gapWidth;
                          return (
                            <div
                              style={{
                                position: 'absolute',
                                left: '1em',
                                bottom: 0,
                                width: `calc(${totalWidth}em)`,
                                height: '2px',
                                backgroundColor: '#000',
                                pointerEvents: 'none'
                              }}
                            />
                          );
                        })()}
                      </div>
                      <span className="guess-indicator">
                        {formatNumber(data.distance)} <ArrowUpIcon style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle' }} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        
        <div className="virtual-keyboard">
          <div className="keyboard-row">
            {['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'].map((letter) => (
              <button
                key={letter}
                onClick={() => handleKeyPress(letter)}
                className="keyboard-key"
                disabled={isGameWon || showHelp}
              >
                {letter.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="keyboard-row">
            {['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'].map((letter) => (
              <button
                key={letter}
                onClick={() => handleKeyPress(letter)}
                className="keyboard-key"
                disabled={isGameWon || showHelp}
              >
                {letter.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="keyboard-row">
            <button
              onClick={() => handleKeyPress('enter')}
              className="keyboard-key keyboard-key-enter"
              disabled={isGameWon || showHelp || currentGuess.length !== 5}
            >
              ENTER
            </button>
            {['z', 'x', 'c', 'v', 'b', 'n', 'm'].map((letter) => (
              <button
                key={letter}
                onClick={() => handleKeyPress(letter)}
                className="keyboard-key"
                disabled={isGameWon || showHelp}
              >
                {letter.toUpperCase()}
              </button>
            ))}
            <button
              onClick={() => handleKeyPress('backspace')}
              className="keyboard-key keyboard-key-backspace"
              disabled={isGameWon || showHelp}
            >
              ⌫
            </button>
          </div>
        </div>
      </div>
      {showSuccess && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowSuccess(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '1em', position: 'relative' }}>
              <button
                onClick={() => setShowSuccess(false)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s ease'
                }}
              >
                <Cross1Icon style={{ width: '24px', height: '24px' }} />
              </button>
              <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontFamily: 'sans-serif' }}>{practiceMode ? 'Practice Complete!' : 'Well done!'}</h2>
            {!practiceMode && (
              <p style={{ marginBottom: '1rem' }}>
                You got it in {guesses.length} {guesses.length === 1 ? 'guess' : 'guesses'}!
              </p>
            )}
            <div className="guess-word" style={{ marginBottom: '1rem', justifyContent: 'flex-start', paddingLeft: 0 }}>
              {currentAnswer && currentAnswer.split('').map((char, idx) => (
                <div key={idx} className="guess-char">
                  {char}
                </div>
              ))}
            </div>
            <div className="success-visualization">
              {guesses
                .filter(guess => guess < currentAnswer)
                .map(guess => getGuessData(guess, guesses))
                .filter(data => data !== null)
                .sort((a, b) => b.distance - a.distance)
                .map((data, idx) => (
                  <div key={`below-${idx}`} className="success-guess-bar">
                    <div 
                      className="success-guess-background"
                      style={{
                        width: `${data.distancePercentage}%`,
                        backgroundColor: data.backgroundColor,
                        borderRightColor: '#90EE90'
                      }}
                    />
                  </div>
                ))}
              {guesses
                .filter(guess => guess > currentAnswer)
                .map(guess => getGuessData(guess, guesses))
                .filter(data => data !== null)
                .sort((a, b) => a.distance - b.distance)
                .map((data, idx) => (
                  <div key={`above-${idx}`} className="success-guess-bar">
                    <div 
                      className="success-guess-background"
                      style={{
                        width: `${data.distancePercentage}%`,
                        backgroundColor: data.backgroundColor,
                        borderRightColor: '#FF6B6B'
                      }}
                    />
                  </div>
                ))}
            </div>
            {!practiceMode && (
              <p style={{ marginBottom: '1.5rem' }}>
                Only {countdown} until word resets.
              </p>
            )}
            {practiceMode ? (
              <button onClick={startPracticeMode} style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                cursor: 'pointer',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px'
              }}>
                Practice Again
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleShare} style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px'
                }}>
                  Share
                </button>
                <button onClick={startPracticeMode} style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  color: '#333',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}>
                  Practice
                </button>
              </div>
            )}
            </div>
            {!practiceMode && day && (
              <div style={{ 
                paddingTop: '1rem',
                paddingBottom: '1rem',
                borderTop: '1px solid #e0e0e0',
                fontSize: '0.85rem',
                color: '#999',
                textAlign: 'center',
                fontFamily: "'Domine', serif"
              }}>
                Abcdle #{day}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
